// Which currency to show a price in, and how to write it.
//
// Stripe is the authority on amounts (see hooks/usePrices.ts and
// billing/prices.ts); this file only decides which of the currencies the
// Prices carry fits the person looking, and formats minor units the way their
// locale expects. Nothing here talks to a server — unit-tested in
// currency.test.ts.

/** Minor units per Stripe: currencies with no fractional unit (amounts are whole). */
const ZERO_DECIMAL = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf',
])

// Time zone → currency, for the currencies the Prices carry manual amounts in.
// A phone's clock is a far stronger signal of where someone lives than its UI
// language: a Swede on an English phone must land on kronor, because the
// currency chosen here is what Checkout will actually charge in.
const TIMEZONE_CURRENCY: Record<string, string> = {
  'Europe/Stockholm': 'sek',
  'Europe/London': 'gbp', 'Europe/Belfast': 'gbp', 'Europe/Jersey': 'gbp', 'Europe/Guernsey': 'gbp', 'Europe/Isle_of_Man': 'gbp',
  'Pacific/Honolulu': 'usd',
  ...Object.fromEntries([
    'Amsterdam', 'Andorra', 'Athens', 'Berlin', 'Bratislava', 'Brussels', 'Busingen', 'Dublin', 'Helsinki',
    'Lisbon', 'Ljubljana', 'Luxembourg', 'Madrid', 'Malta', 'Mariehamn', 'Monaco', 'Nicosia', 'Paris',
    'Riga', 'Rome', 'San_Marino', 'Tallinn', 'Vaduz', 'Vatican', 'Vienna', 'Vilnius', 'Zagreb',
    'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores', 'Asia/Nicosia',
  ].map(z => [z.includes('/') ? z : `Europe/${z}`, 'eur'])),
}

// The rest of the US zones share a prefix; Canada/Mexico do too, but they
// aren't in the manual set, so mapping them to USD would be wrong — only the
// listed names count.
const US_TIMEZONES = new Set([
  'America/New_York', 'America/Detroit', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis', 'America/Indiana/Vincennes', 'America/Indiana/Winamac',
  'America/Indiana/Marengo', 'America/Indiana/Petersburg', 'America/Indiana/Vevay', 'America/Chicago',
  'America/Indiana/Tell_City', 'America/Indiana/Knox', 'America/Menominee', 'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem', 'America/North_Dakota/Beulah', 'America/Denver', 'America/Boise',
  'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'America/Juneau', 'America/Sitka',
  'America/Metlakatla', 'America/Yakutat', 'America/Nome', 'America/Adak', 'US/Eastern', 'US/Central',
  'US/Mountain', 'US/Pacific', 'US/Alaska', 'US/Hawaii', 'US/Arizona',
])

// Region → currency, the weaker second signal (from `navigator.languages`).
const EURO = ['AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES', 'AD', 'MC', 'SM', 'VA', 'ME', 'XK', 'BG']
const REGION_CURRENCY: Record<string, string> = {
  SE: 'sek', NO: 'nok', DK: 'dkk', IS: 'isk', GB: 'gbp', US: 'usd', CH: 'chf', PL: 'pln', CZ: 'czk',
  HU: 'huf', RO: 'ron', CA: 'cad', AU: 'aud', NZ: 'nzd', JP: 'jpy', IN: 'inr', BR: 'brl', MX: 'mxn',
  ZA: 'zar', SG: 'sgd', HK: 'hkd', KR: 'krw',
  ...Object.fromEntries(EURO.map(r => [r, 'eur'])),
}

/** The currency a locale tag implies — `sv` → SE → sek, `en` → US → usd, `de-CH` → chf — or null. */
export function currencyForLocale(tag: string): string | null {
  try {
    const region = new Intl.Locale(tag).maximize().region
    return region ? REGION_CURRENCY[region] ?? null : null
  } catch {
    return null
  }
}

/** The currency a time zone implies, for the manually priced set only. */
export function currencyForTimeZone(zone: string | undefined): string | null {
  if (!zone) return null
  if (US_TIMEZONES.has(zone)) return 'usd'
  return TIMEZONE_CURRENCY[zone] ?? null
}

export interface Signals {
  timeZone?: string
  locales: readonly string[]
}

/**
 * Picks a currency from `available` (what the Prices carry): the time zone's,
 * else the first locale's that is available, else dollars, else the Prices'
 * own base. The base is the account's settlement currency (kronor for a
 * Swedish account), which means little to someone in Tokyo; Checkout may
 * still convert either for them (Adaptive Pricing).
 */
export function detectCurrency(available: readonly string[], signals: Signals, base: string): string {
  const fromZone = currencyForTimeZone(signals.timeZone)
  if (fromZone && available.includes(fromZone)) return fromZone
  for (const tag of signals.locales) {
    const c = currencyForLocale(tag)
    if (c && available.includes(c)) return c
  }
  return available.includes('usd') ? 'usd' : base
}

/** What the browser knows: its clock's zone and its language list. */
export function browserSignals(): Signals {
  let timeZone: string | undefined
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    timeZone = undefined
  }
  const locales = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : []
  return { timeZone, locales }
}

/**
 * Formats a Stripe minor-unit amount: 1900 eur → "€19", 299 eur → "€2.99",
 * 19900 sek in sv-SE → "199 kr", 160 eur → "€1.60".
 */
export function formatMoney(minor: number, currency: string, locale: string): string {
  const code = currency.toUpperCase()
  const amount = ZERO_DECIMAL.has(currency.toLowerCase()) ? minor : minor / 100
  // Whole amounts drop the ".00"; anything else keeps the currency's usual
  // two digits, so €1.60 stays €1.60.
  const whole = Number.isInteger(amount)
  const opts: Intl.NumberFormatOptions = {
    style: 'currency', currency: code, minimumFractionDigits: whole ? 0 : undefined, maximumFractionDigits: 2,
  }
  try {
    return new Intl.NumberFormat(locale, { ...opts, currencyDisplay: 'narrowSymbol' }).format(amount)
  } catch {
    // Older Safari has no narrowSymbol.
    return new Intl.NumberFormat(locale, opts).format(amount)
  }
}

/** "SEK", "EUR" — the label the currency picker shows. */
export function currencyLabel(currency: string): string {
  return currency.toUpperCase()
}

/**
 * The currency of where this phone is — its clock's zone, else its languages,
 * else euro — as an ISO code in upper case: what the trip cost question
 * records (trips.currency). Unlike `detectCurrency` it isn't limited to what
 * the Prices carry.
 */
export function localCurrency(signals: Signals = browserSignals()): string {
  const fromZone = currencyForTimeZone(signals.timeZone)
  if (fromZone) return fromZone.toUpperCase()
  for (const tag of signals.locales) {
    const c = currencyForLocale(tag)
    if (c) return c.toUpperCase()
  }
  return 'EUR'
}

/** A trip's cost as typed (a whole or decimal amount, not minor units), in its currency. */
export function formatCost(amount: number, currency: string, locale?: string): string {
  return formatMoney(Math.round(amount * 100), currency, locale ?? (typeof navigator !== 'undefined' ? navigator.language : 'en'))
}

/** A typed amount — "845", "845,50", "1 245.5" — or null when it isn't one. */
export function parseCost(text: string): number | null {
  const t = text.replace(/[\s\u00a0']/g, '').replace(',', '.')
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(t)) return null
  return Number(t)
}
